import { useEffect, useState } from "react";
import { Play, Timer, ListOrdered } from "lucide-react";
import { http, errMsg } from "../../api";
import AttemptBadge from "./AttemptBadge";
import ExamRunner from "./ExamRunner";

function ResultCard({ attempt }) {
  return (
    <div className={`result-card ${attempt.passed ? "pass" : "fail"}`} data-testid="language-result">
      <strong>{attempt.passed ? "Test cleared" : "Test not cleared"}</strong>
      <span className="result-pct">{attempt.percent}%</span>
      <small>{attempt.correct} of {attempt.total} correct{attempt.auto_submitted === "tab_switch" ? " · auto-submitted because the test window was left too many times" : ""}{attempt.passed ? " · the next step is unlocked" : " · the Vocalhouse team may allow another attempt"}</small>
    </div>
  );
}

export default function LanguageTest({ onChanged }) {
  const [data, setData] = useState(null);
  const [exam, setExam] = useState(null);
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState("");

  const load = () => http.get("/creator/tests/language").then(r => setData(r.data)).catch(e => setNotice(errMsg(e)));
  useEffect(() => { load(); }, []);

  const start = async lang => {
    setNotice("");
    try { const { data: d } = await http.post(`/creator/tests/language/${encodeURIComponent(lang)}/start`); setExam(d); }
    catch (e) { setNotice(errMsg(e)); }
  };
  const finished = res => { setExam(null); setResult(res.attempt); load(); onChanged(); };

  if (exam) return <ExamRunner exam={exam} onFinished={finished} />;
  if (!data) return notice ? <div className="notice error">{notice}</div> : <div className="loading">Loading tests…</div>;
  return (
    <section className="form-card" data-testid="creator-language-step">
      <h2>Step 2 · Language &amp; comprehension test</h2>
      <p>One attempt per language. Timed multiple-choice questions (10 minutes per 15 questions). Switching tabs or windows is tracked — after the allowed warnings the test is submitted automatically. Scores are calculated securely on our server; answer keys are never shown.</p>
      {result && <ResultCard attempt={result} />}
      {notice && <div className="notice error" data-testid="language-test-notice">{notice}</div>}
      <div className="test-list">
        {data.items.map(t => (
          <div className="test-card" key={t.language} data-testid={`language-test-${t.language}`}>
            <div className="test-card-head"><strong>{t.language}</strong><AttemptBadge attempt={t.attempt} required={t.required} testid={`language-test-status-${t.language}`} /></div>
            {t.required ? (
              <>
                <p className="muted"><ListOrdered size={13} /> {t.questions_count} questions · <Timer size={13} /> {t.time_limit_minutes} minutes · auto-submits when time ends</p>
                {t.can_start && <button className="primary" onClick={() => start(t.language)} data-testid={`start-language-test-${t.language}`}><Play size={15} /> {t.attempt ? "Resume test" : `Start ${t.language} test`}</button>}
              </>
            ) : <p className="muted">No language test is required for {t.language}.</p>}
          </div>
        ))}
      </div>
      {data.status === "unavailable" && <div className="notice warning" data-testid="language-test-unavailable">Tests for your languages are not published yet. You can start as soon as the admin publishes them.</div>}
    </section>
  );
}
